import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  contentChild,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import {
  ConnectedPosition,
  Overlay,
  OverlayConfig,
  OverlayContainer,
  OverlayRef,
  ScrollStrategy
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { DOWN_ARROW, ENTER, ESCAPE, TAB, UP_ARROW } from '@angular/cdk/keycodes';

import { BsAutocompletePanelComponent, FooterMode } from './bs-autocomplete-panel.component';
import { BsEmptyDirective, BsFooterDirective, BsItemDirective } from './bs-autocomplete.directives';
import { BS_ZINDEX_SERVICE } from './bs-zindex.service';

let nextId = 0;

@Component({
  selector: 'bs-autocomplete',
  standalone: true,
  imports: [],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BsAutocompleteComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BsAutocompleteComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div #containerEl class="bs-ac-container input-group" [class]="containerClass()">
      <input
        #inputEl
        type="text"
        class="form-control"
        [class]="inputClass()"
        [id]="inputId()"
        [placeholder]="placeholder()"
        [disabled]="disabledState()"
        [readonly]="readonly()"
        [attr.maxlength]="maxlength()"
        [value]="text()"
        [attr.aria-expanded]="overlayOpen()"
        [attr.aria-controls]="overlayOpen() ? listboxId() : null"
        [attr.aria-activedescendant]="activeDescendantId()"
        role="combobox"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        autocomplete="off"
        (input)="onInput($event)"
        (focus)="onFocus()"
        (blur)="onBlur()"
        (keydown)="onKeydown($event)"
        (compositionstart)="onCompositionStart()"
        (compositionend)="onCompositionEnd($event)"
      />

      @if (showClearButton()) {
        <button
          type="button"
          class="btn btn-outline-secondary bs-ac-clear"
          [disabled]="disabledState()"
          (mousedown)="$event.preventDefault()"
          (click)="onClear()"
          aria-label="Clear"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      }

      @if (dropdown()) {
        <button
          type="button"
          class="btn btn-outline-secondary bs-ac-dropdown"
          [disabled]="disabledState()"
          (mousedown)="$event.preventDefault()"
          (click)="onDropdownClick()"
          aria-label="Toggle dropdown"
        >
          <span aria-hidden="true">&#9662;</span>
        </button>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }

    .bs-ac-container {
      position: relative;
    }

    .bs-ac-clear,
    .bs-ac-dropdown {
      z-index: 1;
    }
  `],
  host: {
    'class': 'bs-autocomplete'
  }
})
export class BsAutocompleteComponent<T>
  implements ControlValueAccessor, Validator, OnInit, OnDestroy
{
  private overlay = inject(Overlay);
  private overlayContainer = inject(OverlayContainer);
  private cdr = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef);
  private zIndexService = inject(BS_ZINDEX_SERVICE);

  // ViewChild refs
  readonly inputElRef = viewChild.required<ElementRef<HTMLInputElement>>('inputEl');
  readonly containerElRef = viewChild.required<ElementRef<HTMLElement>>('containerEl');

  // ContentChild refs
  readonly itemDirective = contentChild(BsItemDirective);
  readonly emptyDirective = contentChild(BsEmptyDirective);
  readonly footerDirective = contentChild(BsFooterDirective);

  // Signal inputs
  readonly inputId = input(`bs-ac-input-${nextId++}`);
  readonly placeholder = input('');
  readonly items = input<T[]>([]);
  readonly loading = input(false);
  readonly disabled = input(false);
  readonly dropdown = input(false);
  readonly minLength = input(1);
  readonly debounceMs = input(250);
  readonly requireSelection = input(false);
  readonly emptyText = input('No results');
  readonly panelMaxHeight = input('300px');
  readonly panelClass = input<string | string[]>('');
  readonly inputClass = input<string | string[]>('');
  readonly containerClass = input<string | string[]>('');
  readonly openOnFocus = input(false);
  readonly footerMode = input<FooterMode>('always');
  readonly showClear = input(true);
  readonly multiple = input(false);
  readonly maxlength = input<number | null>(null);
  readonly delay = input(250);
  readonly readonly = input(false);
  readonly autoZIndex = input(true);
  readonly baseZIndex = input(0);

  readonly displayWith = input<(value: T | null) => string>((v) =>
    v == null ? '' : String(v)
  );
  readonly compareWith = input<(a: T, b: T) => boolean>((a, b) => a === b);

  // Outputs
  readonly queryChange = output<string>();
  readonly selected = output<T>();
  readonly cleared = output<void>();
  readonly opened = output<void>();
  readonly closed = output<void>();
  readonly blurred = output<void>();

  // Internal state as signals
  readonly value = signal<T | null>(null);
  readonly text = signal('');
  readonly overlayOpen = signal(false);
  readonly activeDescendantId = signal<string | null>(null);
  readonly disabledState = signal(false);

  // Computed
  readonly listboxId = computed(() => `${this.inputId()}-listbox`);
  readonly showClearButton = computed(() =>
    this.showClear() && !this.disabledState() && (this.text().length > 0 || this.value() != null)
  );

  // Internal state for open on focus
  private openOnFocusInternal = false;

  private overlayRef: OverlayRef | null = null;
  private panelInstance: BsAutocompletePanelComponent<T> | null = null;
  private isComposing = false;
  private skipNextQuery = false;
  private destroy$ = new Subject<void>();
  private query$ = new Subject<string>();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: T | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  constructor() {
    // Effect to update panel when items or loading changes
    effect(() => {
      const items = this.items();
      const loading = this.loading();
      if (this.panelInstance) {
        this.panelInstance.items.set(items ?? []);
        this.panelInstance.loading.set(loading);
        this.panelInstance.query.set(this.text());
      }
    }, {allowSignalWrites: true});
  }

  ngOnInit(): void {
    this.setupQueryDebounce();
    this.setupOpenOnFocusDefault();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.autoZIndex()) {
      this.zIndexService.clear(this.getOverlayContainerElement());
    }
    this.closePanel();
    this.overlayRef?.dispose();
  }

  private setupQueryDebounce(): void {
    // Use delay() if provided, otherwise use debounceMs()
    const debounce = this.delay() > 0 ? this.delay() : this.debounceMs();
    this.query$
      .pipe(
        debounceTime(debounce),
        distinctUntilChanged(),
        filter(() => !this.isComposing),
        takeUntil(this.destroy$)
      )
      .subscribe((query) => {
        this.queryChange.emit(query);
      });
  }

  private setupOpenOnFocusDefault(): void {
    if (this.minLength() === 0 && !this.openOnFocus()) {
      this.openOnFocusInternal = true;
    } else {
      this.openOnFocusInternal = this.openOnFocus();
    }
  }

  // ControlValueAccessor
  writeValue(value: T | null): void {
    this.value.set(value);
    this.text.set(this.displayWith()(value));
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledState.set(isDisabled);
    if (isDisabled) {
      this.closePanel();
    }
    this.cdr.markForCheck();
  }

  // Validator
  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.requireSelection() && this.value() == null && this.text().trim().length > 0) {
      return { requireSelection: true };
    }
    return null;
  }

  // Input events
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    // If a value is selected and user modifies the text, clear the selection
    // but DON'T clear text/input — fall through to process the new character
    if (this.value() != null && input.value !== this.displayWith()(this.value())) {
      this.value.set(null);
      this.onChange(null);
      this.cleared.emit();
    }

    this.text.set(input.value);

    if (!this.isComposing) {
      if (this.text().length >= this.minLength()) {
        this.openPanel();
        this.query$.next(this.text());
      } else if (this.text().length === 0 && this.minLength() === 0) {
        this.openPanel();
      } else {
        this.closePanel();
      }
    }
  }

  onFocus(): void {
    if (this.skipNextQuery) {
      this.skipNextQuery = false;
      return;
    }
    if (this.openOnFocusInternal && !this.overlayOpen() && !this.disabledState()) {
      this.openPanel();
    }
  }

  onBlur(): void {
    this.onTouched();
    this.blurred.emit();

    // Handle requireSelection on blur
    if (this.requireSelection() && this.value() == null && this.text().trim().length > 0) {
      this.text.set('');
      this.cdr.markForCheck();
    }

    // Delay close to allow click events on panel
    setTimeout(() => {
      const activeEl = document.activeElement;
      const containsActive = this.elementRef.nativeElement.contains(activeEl);
      if (!containsActive) {
        this.closePanel();
      }
    }, 150);
  }

  onCompositionStart(): void {
    this.isComposing = true;
  }

  onCompositionEnd(event: CompositionEvent): void {
    this.isComposing = false;
    this.text.set((event.target as HTMLInputElement).value);
    if (this.text().length >= this.minLength()) {
      this.openPanel();
    }
  }

  onKeydown(event: KeyboardEvent): void {
    const keyCode = event.keyCode;

    if (keyCode === DOWN_ARROW) {
      event.preventDefault();
      if (!this.overlayOpen()) {
        this.openPanel();
      } else {
        this.panelInstance?.keyManager.onKeydown(event);
        this.updateActiveDescendant();
        this.panelInstance?.scrollActiveOptionIntoView();
      }
    } else if (keyCode === UP_ARROW) {
      event.preventDefault();
      if (!this.overlayOpen()) {
        this.openPanel();
      } else {
        this.panelInstance?.keyManager.onKeydown(event);
        this.updateActiveDescendant();
        this.panelInstance?.scrollActiveOptionIntoView();
      }
    } else if (keyCode === ENTER) {
      event.preventDefault();
      if (this.overlayOpen() && this.panelInstance?.keyManager.activeItem) {
        const activeItem = this.panelInstance.keyManager.activeItem;
        this.selectOption(activeItem.value);
      }
    } else if (keyCode === ESCAPE) {
      event.preventDefault();
      this.closePanel();
    } else if (keyCode === TAB) {
      this.closePanel();
    }
  }

  onClear(): void {
    this.value.set(null);
    this.text.set('');
    this.onChange(null);
    this.cleared.emit();
    this.inputElRef().nativeElement.focus();
    this.cdr.markForCheck();
  }

  onDropdownClick(): void {
    if (this.overlayOpen()) {
      this.closePanel();
    } else {
      this.openPanel();
    }
    this.inputElRef().nativeElement.focus();
  }

  selectOption(item: T): void {
    this.value.set(item);
    this.text.set(this.displayWith()(item));
    this.onChange(item);
    this.selected.emit(item);
    this.closePanel();
    this.skipNextQuery = true;
    this.inputElRef().nativeElement.focus();
    this.cdr.markForCheck();
  }

  // Overlay management
  private openPanel(): void {
    if (this.overlayOpen() || this.disabledState()) {
      return;
    }

    if (!this.overlayRef) {
      this.createOverlay();
    }

    try {
      const portal = new ComponentPortal(BsAutocompletePanelComponent);
      const componentRef = this.overlayRef!.attach(portal);
      this.panelInstance = componentRef.instance as BsAutocompletePanelComponent<T>;
    } catch (e) {
      return;
    }

    this.updatePanelProperties();
    this.updateOverlayWidth();

    if (this.autoZIndex()) {
      this.zIndexService.set('overlay', this.getOverlayContainerElement(), this.baseZIndex());
    }

    this.overlayOpen.set(true);
    this.opened.emit();
    this.query$.next(this.value() != null ? '' : this.text());
    this.cdr.markForCheck();

    // Setup backdrop click handler
    this.overlayRef!.backdropClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.closePanel());
  }

  private closePanel(): void {
    if (!this.overlayOpen()) return;

    if (this.autoZIndex()) {
      this.zIndexService.clear(this.getOverlayContainerElement());
    }
    this.overlayRef?.detach();
    this.panelInstance = null;
    this.overlayOpen.set(false);
    this.activeDescendantId.set(null);
    this.closed.emit();
    this.cdr.markForCheck();
  }

  private createOverlay(): void {
    const positions: ConnectedPosition[] = [
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top'
      },
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom'
      }
    ];

    const scrollStrategy: ScrollStrategy = this.overlay.scrollStrategies.reposition();
    const rect = this.containerElRef().nativeElement.getBoundingClientRect();
    const width = rect.width;

    const config: OverlayConfig = {
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.containerElRef())
        .withPositions(positions)
        .withPush(true),
      scrollStrategy,
      width,
      hasBackdrop: true,
      backdropClass: 'bs-ac-backdrop'
    };

    this.cdr.markForCheck();

    this.overlayRef = this.overlay.create(config);

    // Handle window resize
    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.overlayOpen()) {
          this.updateOverlayWidth();
        }
      });
  }

  private updateOverlayWidth(): void {
    if (this.overlayRef && this.containerElRef()) {
      const width = this.containerElRef().nativeElement.getBoundingClientRect().width;
      this.overlayRef.updateSize({ width });
    }
    this.cdr.markForCheck();
  }

  private updatePanelProperties(): void {
    if (!this.panelInstance) return;

    this.panelInstance.items.set(this.items() ?? []);
    this.panelInstance.loading.set(this.loading());
    this.panelInstance.query.set(this.text());
    this.panelInstance.listboxId.set(this.listboxId());
    this.panelInstance.maxHeight.set(this.panelMaxHeight());
    this.panelInstance.emptyText.set(this.emptyText());
    this.panelInstance.footerMode.set(this.footerMode());
    this.panelInstance.displayWith.set(this.displayWith());
    this.panelInstance.itemTemplate.set(this.itemDirective()?.templateRef ?? null);
    this.panelInstance.emptyTemplate.set(this.emptyDirective()?.templateRef ?? null);
    this.panelInstance.footerTemplate.set(this.footerDirective()?.templateRef ?? null);
    this.panelInstance.onSelect = (item: T) => this.selectOption(item);
    this.cdr.markForCheck();
  }

  private updateActiveDescendant(): void {
    const activeItem = this.panelInstance?.keyManager.activeItem;
    this.activeDescendantId.set(activeItem?.id ?? null);
    this.cdr.markForCheck();
  }

  private getOverlayContainerElement(): HTMLElement {
    return this.overlayContainer.getContainerElement();
  }

  // Public method to update items (called from parent when items change)
  updateItems(items: T[]): void {
    if (this.panelInstance) {
      this.panelInstance.items.set(items ?? []);
      this.panelInstance.loading.set(this.loading());
    }
  }

  // Public method to programmatically show the panel
  show(): void {
    this.openPanel();
  }

  // Public method to programmatically hide the panel
  hide(): void {
    this.closePanel();
  }
}
